import React, { useEffect, useState, useRef, useCallback } from "react";
import useIsomorphicLayoutEffect from "./useIsomorphicLayoutEffect";

function useDebounce<T>(value: T, delay?: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay || 500);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

const useCounter = (initialState = 0) => {
  const [count, setCount] = useState(initialState);
  const add = () => setCount(count + 1);
  const subtract = () => setCount(count - 1);
  return { count, add, subtract };
};

function useTimeout(callback: () => void, delay: number | null) {
  const savedCallback = useRef(callback);

  // Remember the latest callback if it changes.
  useIsomorphicLayoutEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the timeout.
  useEffect(() => {
    // Don't schedule if no delay is specified.
    // Note: 0 is a valid value for delay.
    if (!delay && delay !== 0) {
      return;
    }

    const id = setTimeout(() => savedCallback.current(), delay);

    return () => clearTimeout(id);
  }, [delay]);
}

function useStateCallback<T>(initialState: T): [T, (state: T, cb?: (state: T) => void) => void] {
  const [state, setState] = useState(initialState);
  const cbRef = useRef<((state: T) => void) | undefined>(undefined); // init mutable ref container for callbacks

  const setStateCallback = useCallback((state: T, cb?: (state: T) => void) => {
    cbRef.current = cb; // store current, passed callback in ref
    setState(state);
  }, []); // keep object reference stable, exactly like `useState`

  useEffect(() => {
    // cb.current is `undefined` on initial render,
    // so we only invoke callback on state *updates*
    if (cbRef.current) {
      cbRef.current(state);
      cbRef.current = undefined; // reset callback after execution
    }
  }, [state]);

  return [state, setStateCallback];
}

/**
 * Defines the custom dispatch function that wraps the dispatch returned from useReducer.
 */
type CustomReactDispatchWithCallback<A, S> = (action: A, callback?: DispatchCallback<S>) => void;

/**
 * Defines the callback contract. It should be a function that receives the updated state.
 */
type DispatchCallback<S> = (state: S) => void;

/**
 * Wraps `React.useReducer` and provides a custom dispatch function that accepts
 * a callback that will be cached and then invoked when the reducer state changes.
 */
function useReducerWithCallback<R extends React.Reducer<any, any>, I>(reducer: R, initialState: I & React.ReducerState<R>) {
  const callbackRef = React.useRef<DispatchCallback<React.ReducerState<R>>>();

  const [state, dispatch] = React.useReducer(reducer, initialState);

  React.useEffect(() => {
    callbackRef.current?.(state);
  }, [state]);

  const customDispatch: CustomReactDispatchWithCallback<React.ReducerAction<R>, React.ReducerState<R>> = (action, callback) => {
    callbackRef.current = callback;
    dispatch(action);
  };

  return [state, customDispatch] as const;
}

const convertDateToText = (input: Date) => {
  if (Object.prototype.toString.call(input) === "[object Date]") {
    if (isNaN(input.getTime())) {
      return "Invalid date";
    }
  } else {
    return "Invalid date";
  }

  let output: string = "";

  const weekday = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const getOrdinalNum = (n: number) => {
    return n + (n > 0 ? ["th", "st", "nd", "rd"][(n > 3 && n < 21) || n % 10 > 3 ? 0 : n % 10] : "");
  };
  const dateYesterday = () => {
    const intermedDate = new Date();
    intermedDate.setDate(intermedDate.getDate() - 1);
    return intermedDate;
  };
  const yesterday = input.toDateString() === dateYesterday().toDateString();
  const today = input.toDateString() === new Date().toDateString();
  const thisYear = input.getFullYear() === new Date().getFullYear();

  output = thisYear ? `${weekday[input.getDay()]}, ${months[input.getMonth()]} ${getOrdinalNum(input.getDate())}` : `${months[input.getMonth()]} ${getOrdinalNum(input.getDate())}, ${input.getFullYear()}`;

  return today ? "Today" : yesterday ? "Yesterday" : output;
};

/*!
 * Group items from an array together by some criteria or value.
 * (c) 2019 Tom Bremmer (https://tbremer.com/) and Chris Ferdinandi (https://gomakethings.com), MIT License,
 * @param  {Array}           arr      The array to group items from
 * @param  {String|Function} criteria The criteria to group by
 * @return {Object}                   The grouped object
 */
const groupBy = function (arr: any, criteria: any) {
  return arr.reduce(function (obj: any, item: any) {
    // Check if the criteria is a function to run on the item or a property of it
    var key = typeof criteria === "function" ? criteria(item) : item[criteria];

    // If the key doesn't exist yet, create it
    if (!obj.hasOwnProperty(key)) {
      obj[key] = [];
    }

    // Push the value to the object
    obj[key].push(item);

    // Return the object to the next item in the loop
    return obj;
  }, {});
};
/*export default useDebounce;*/
export { useDebounce, useCounter, useTimeout, useStateCallback, useReducerWithCallback, groupBy, convertDateToText };
